import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import CONFIG from '../config';
import AppointmentCalendar from './Calendar';
import { useAuth } from '../context/AuthProvider';
import { usePatient } from '../context/PatientContext';

const MyCalendarView = () => {
  const { verifyTokenAndRedirect } = useAuth();
  const { doctorInfo } = usePatient();

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Verify auth token once
  useEffect(() => {
    verifyTokenAndRedirect();
  }, [verifyTokenAndRedirect]);

  // ✅ Stable API call (memoized)
  const handleSaveNewAppointment = async (appointmentData) => {
  try {
    const response = await axios.post(
      `${CONFIG.BASE_URL}/doctors/appointment/schedule`,
      appointmentData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          doctorId: doctorInfo.MANDALI_ID,
          clinicId: doctorInfo.CLINIC_ID,
          roleId: doctorInfo.MANDALI_ROLE,
          'Content-Type': 'application/json',
        },
      }
    );

    if (response.data.success) {
      console.log('✅ Appointment scheduled successfully');
      // Refresh appointments
      await fetchAppointments();
    }
  } catch (error) {
    console.error('❌ Error scheduling appointment:', error);
    alert('Failed to schedule appointment. Please try again.');
  }
};
  const fetchAppointments = useCallback(
    async (date = new Date()) => {
      if (!doctorInfo?.MANDALI_ID) {
        console.warn('⚠️ Doctor info not available yet');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const dateStr =
          date instanceof Date
            ? date.toISOString().split('T')[0]
            : new Date(date).toISOString().split('T')[0];

        console.log('🔄 Fetching appointments for:', dateStr);

        const response = await axios.get(`${CONFIG.BASE_URL}/doctors/calendar/appointments`, {
          params: { view: 'month', date: dateStr },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            doctorId: doctorInfo.MANDALI_ID,
            clinicId: doctorInfo.CLINIC_ID,
            roleId: doctorInfo.MANDALI_ROLE,
            'Content-Type': 'application/json',
          },
        });

        if (response.data.success) {
          console.log('✅ Appointments loaded:', response.data);
          
          setAppointments(response.data.appointments);
        } else {
          console.error('❌ API returned success:false');
          setError('Failed to fetch appointments.');
        }
      } catch (err) {
        console.error('❌ Error fetching appointments:', err);
        setError('Unable to fetch appointments. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [doctorInfo?.MANDALI_ID, doctorInfo?.CLINIC_ID, doctorInfo?.MANDALI_ROLE]
  );

  // ✅ Initial fetch only once doctor info is ready
  useEffect(() => {
    if (doctorInfo?.MANDALI_ID) {
      fetchAppointments();
    }
  }, [doctorInfo?.MANDALI_ID, fetchAppointments]);

  // ✅ Memoized props (avoid unnecessary re-renders)
  const memoizedAppointments = useMemo(() => appointments, [appointments]);
  const memoizedDoctorInfo = useMemo(() => doctorInfo, [doctorInfo?.MANDALI_ID]);

  // ✅ Handler for calendar item click
  const handleAppointmentClick = (appointment) => {
    console.log('🩺 Appointment clicked:', appointment);
  };

  // ✅ Persistent layout — Calendar is never conditionally removed
  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div
        style={{
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div>
          <h1
            style={{
              fontSize: '28px',
              fontWeight: '600',
              color: '#2c3e50',
              marginBottom: '10px',
            }}
          >
            My Calendar
          </h1>
          <p
            style={{
              color: '#7f8c8d',
              fontSize: '16px',
              margin: 0,
            }}
          >
            View and manage your appointments
          </p>
        </div>

        <button
          onClick={() => fetchAppointments()}
          disabled={loading}
          style={{
            padding: '10px 20px',
            backgroundColor: loading ? '#bdc3c7' : '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s',
          }}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Inline feedback instead of conditional returns */}
      {error && (
        <div
          style={{
            background: '#fdecea',
            color: '#e74c3c',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '15px',
          }}
        >
          {error}
          <button
            onClick={() => fetchAppointments()}
            style={{
              marginLeft: '12px',
              backgroundColor: '#e74c3c',
              color: 'white',
              border: 'none',
              padding: '6px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {loading && appointments.length === 0 && (
        <p style={{ color: '#7f8c8d', fontSize: '16px', textAlign: 'center', marginBottom: '10px' }}>
          Loading appointments...
        </p>
      )}

      {/* Always rendered Calendar — stable and persistent */}
      <AppointmentCalendar
        mode="edit"
        existingAppointments={memoizedAppointments}
        onAppointmentClick={handleAppointmentClick}
        doctorInfo={memoizedDoctorInfo}
        loading={loading}
        fetchAppointment={fetchAppointments}
        userRole={doctorInfo?.MANDALI_ROLE || 3}
        clinicId={doctorInfo?.CLINIC_ID}
        isStandaloneCalendar={true}
        onSaveAppointment={handleSaveNewAppointment} 
      />
    </div>
  );
};

export default MyCalendarView;
